import { pipToDecimal } from '@katanaperps/katana-perps-sdk';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import {
  useVaultOnChainQuantityAvailableForExitWithdrawalQuery,
  useVaultOnChainSummaryQuery,
  useVaultOnChainWithdrawalQueueQuery,
} from '#/api/queries/vaultsOnChainQueries';
import { useVaultQuery } from '#/api/queries/vaultsQueries';
import LabelClickable from '#/components/basic/Button/LabelClickable';
import DataPair from '#/components/basic/panels/DataPair';
import TextColored from '#/components/basic/text/TextColored';
import MoreInfoTooltip from '#/components/basic/tooltip/MoreInfoTooltip';
import { useNow } from '#/hooks/useNow';
import { formatDateTime } from '#/lib/date';
import { abbreviateNumber, formatUsdWithSign } from '#/lib/display/formatting';

import VaultIconSvg from '../images/VaultIconSvg';
import {
  calculateVaultExitAgeRemaining,
  calculateVaultTresholds,
  formatVaultExitAgeHours,
  formatVaultExitAgeRemaining,
} from '../shared/utils';
import {
  vaultConfigWithdrawalExitAgeDescription,
  vaultDepositorObligationsDescription,
  vaultDepositsCapDescription,
  vaultExitThresholdDescription,
  vaultExitValueDescription,
  vaultWithdrawalRequestsDescription,
  vaultWithdrawalThresholdDescription,
} from '../shared/vaultSharedTooltips';

const Wrap = styled.div<{ $inEndStateFrame?: boolean }>`
  margin: ${props => (props.$inEndStateFrame ? '0' : '14px 0 8px')};
  padding: 8px;
  border-radius: 2px;
  background: var(--black1b);
`;

const EndStateFrame = styled.div<{ $variant: 'liquidated' | 'exited' }>`
  margin: 14px 0 8px;
  padding: 0 2px 2px;
  border-radius: 2px;
  background: ${props =>
    props.$variant === 'liquidated' ? 'var(--red1d)' : 'var(--yellow-dark)'};
`;

const EndStateHeader = styled.div<{ $variant: 'liquidated' | 'exited' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 24px;
  padding: 0 6px;
  color: ${props =>
    props.$variant === 'liquidated' ? 'var(--red3)' : 'var(--yellow0)'};
  font-size: var(--small);
`;

const TitleFlex = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 1px 0 8px;

  color: var(--gray4d);
  font-size: var(--smallest);
  font-style: normal;
  font-weight: normal;
  line-height: 1;
  text-transform: uppercase;
`;

export default function VaultStatsPanel({
  managerWallet,
}: {
  managerWallet: string;
}) {
  const { data: summaryData } = useVaultOnChainSummaryQuery({
    managerWallet,
  });
  const { data: vaultData } = useVaultQuery({ managerWallet });
  const { data: withdrawalQueueData } = useVaultOnChainWithdrawalQueueQuery({
    managerWallet,
    stateAggregator: vaultData?.stateAggregator,
  });
  const { data: quantityAvailableForExitWithdrawalData } =
    useVaultOnChainQuantityAvailableForExitWithdrawalQuery({ managerWallet });

  const isLiquidated = !!vaultData?.liquidatedTime || summaryData?.isLiquidated;
  const isExited = !!vaultData?.exitedTime || summaryData?.isExited;
  const endState = isLiquidated
    ? 'liquidated'
    : isExited
    ? 'exited'
    : undefined;
  const endStateTime = isLiquidated
    ? vaultData?.liquidatedTime
    : vaultData?.exitedTime;

  const formatUsdOrDash = (value?: number | string) =>
    value === undefined ? '--' : formatUsdWithSign(Number(value));

  const { maxNetDeposits, depositorNetDeposits, totalOwedQuantity } =
    useMemo(() => {
      return {
        totalOwedQuantity: summaryData
          ? pipToDecimal(summaryData.totalOwedQuantity)
          : undefined,
        depositorNetDeposits: summaryData
          ? pipToDecimal(summaryData.depositorNetDeposits)
          : undefined,
        maxNetDeposits:
          summaryData?.configurationFieldsTransformed.maximumNetDeposits,
      };
    }, [summaryData]);

  const withdrawalRequestsUsd = useMemo(() => {
    if (!withdrawalQueueData) {
      return undefined;
    }
    const sumBN =
      withdrawalQueueData.reduce(
        (sum: bigint, item) => sum + (item?.grossQuantity ?? 0n),
        0n
      ) || 0n;
    return pipToDecimal(sumBN);
  }, [withdrawalQueueData]);

  const { exitThresholdValue, withdrawalThresholdValue } =
    calculateVaultTresholds(summaryData?.configurationFieldsTransformed, {
      totalOwedQuantity: summaryData?.totalOwedQuantity,
    });

  // Countdown shows minutes, the default 10 s tick just keeps the flip to
  // "Exit available" timely
  const now = useNow();
  const exitAgeInS =
    summaryData?.configurationFieldsTransformed
      ?.minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit;
  const exitAgeRemaining = useMemo(
    () =>
      endState
        ? undefined
        : calculateVaultExitAgeRemaining(exitAgeInS, withdrawalQueueData, now),
    [endState, exitAgeInS, withdrawalQueueData, now]
  );

  const depositsDivCapPercentage =
    maxNetDeposits && depositorNetDeposits
      ? (Number(depositorNetDeposits) / Number(maxNetDeposits)) * 10000
      : undefined;

  const panel = (
    <Wrap $inEndStateFrame={!!endState}>
      <TitleFlex>
        <VaultIconSvg color="var(--gray4d)" marginTop="-1px" />
        Fixed Income V1
      </TitleFlex>
      <DataPair
        label={
          <MoreInfoTooltip
            fixedPosition
            tooltip={vaultDepositorObligationsDescription}
          >
            Depositor Obligations
          </MoreInfoTooltip>
        }
        noPadding
      >
        {formatUsdOrDash(totalOwedQuantity)}
      </DataPair>
      <DataPair
        label={
          <MoreInfoTooltip fixedPosition tooltip={vaultDepositsCapDescription}>
            Deposits / Cap
          </MoreInfoTooltip>
        }
        noPadding
      >
        <TextColored
          color={
            !depositsDivCapPercentage
              ? 'default'
              : depositsDivCapPercentage > 99
              ? 'yellow'
              : depositsDivCapPercentage > 90
              ? 'yellow'
              : 'default'
          }
        >
          {formatUsdOrDash(depositorNetDeposits)}{' '}
        </TextColored>{' '}
        /{' '}
        {maxNetDeposits
          ? `$${abbreviateNumber(Number(maxNetDeposits), 2)}`
          : '--'}
      </DataPair>
      <DataPair
        label={
          <MoreInfoTooltip
            fixedPosition
            tooltip={vaultWithdrawalThresholdDescription}
          >
            Withdrawal Threshold
          </MoreInfoTooltip>
        }
        noPadding
      >
        {formatUsdOrDash(withdrawalThresholdValue)}
      </DataPair>
      <DataPair
        label={
          <MoreInfoTooltip
            fixedPosition
            tooltip={vaultWithdrawalRequestsDescription}
          >
            Withdrawal Requests
          </MoreInfoTooltip>
        }
        noPadding
      >
        <TextColored
          color={
            withdrawalRequestsUsd &&
            quantityAvailableForExitWithdrawalData?.quantityInDecimal &&
            Number(withdrawalRequestsUsd) >
              Number(quantityAvailableForExitWithdrawalData.quantityInDecimal)
              ? 'red'
              : 'default'
          }
        >
          {formatUsdOrDash(withdrawalRequestsUsd)}
        </TextColored>
      </DataPair>
      <DataPair
        label={
          <MoreInfoTooltip
            fixedPosition
            tooltip={vaultConfigWithdrawalExitAgeDescription}
          >
            Withdrawal Exit Age
          </MoreInfoTooltip>
        }
        noPadding
      >
        {exitAgeInS === undefined
          ? '--'
          : `${formatVaultExitAgeHours(exitAgeInS)}h`}
        {exitAgeRemaining && (
          <LabelClickable
            type={exitAgeRemaining.ended ? 'error' : 'warning'}
            margin="0 0 0 6px"
          >
            {exitAgeRemaining.ended
              ? 'Exit available'
              : `${formatVaultExitAgeRemaining(exitAgeRemaining)} left`}
          </LabelClickable>
        )}
      </DataPair>
      <DataPair
        label={
          <MoreInfoTooltip
            fixedPosition
            tooltip={vaultExitThresholdDescription}
          >
            Exit Threshold
          </MoreInfoTooltip>
        }
        noPadding
      >
        {exitThresholdValue === undefined
          ? '--'
          : formatUsdWithSign(exitThresholdValue)}
      </DataPair>
      <DataPair
        label={
          <MoreInfoTooltip fixedPosition tooltip={vaultExitValueDescription}>
            Exit Value
          </MoreInfoTooltip>
        }
        noPadding
      >
        <TextColored
          color={
            quantityAvailableForExitWithdrawalData?.quantityInDecimal &&
            exitThresholdValue !== undefined &&
            Number(quantityAvailableForExitWithdrawalData.quantityInDecimal) <
              exitThresholdValue * 1.1 // < 110% of Exit Threshold
              ? 'red'
              : 'default'
          }
        >
          {quantityAvailableForExitWithdrawalData
            ? formatUsdWithSign(
                quantityAvailableForExitWithdrawalData.quantityInDecimal
              )
            : '--'}
        </TextColored>
      </DataPair>
    </Wrap>
  );

  if (!endState) {
    return panel;
  }

  return (
    <EndStateFrame $variant={endState}>
      <EndStateHeader $variant={endState}>
        <b>{endState === 'liquidated' ? 'Liquidated' : 'Exited'}</b>
        {endStateTime ? <span>{formatDateTime(endStateTime)}</span> : null}
      </EndStateHeader>
      {panel}
    </EndStateFrame>
  );
}
